import { validate } from 'validate.js'

import Rails from '@rails/ujs'

function addErrorMessage (messages, error) {
  const block = document.createElement('label')
  block.classList.add('error')
  block.innerText = error

  const inputHint = messages.querySelector('.form-input-hint')

  if (inputHint) {
    messages.insertBefore(block, inputHint)
  } else {
    messages.appendChild(block)
  }
}

function resetFormGroup (formGroup, input) {
  if (!input.classList.contains('error')) return

  const groupErrorEls = formGroup.querySelectorAll('.error')

  if (!groupErrorEls.length) return

  groupErrorEls.forEach(el => {
    if (el.tagName === 'LABEL') {
      el.remove()
    } else {
      el.classList.remove('error')
    }
  })
}

function showErrorsForInput (input, errors, inputNumber) {
  const formGroup = input.closest('.form-input')

  resetFormGroup(formGroup, input)

  if (errors) {
    input.classList.add('error')

    if (inputNumber === 0) {
      errors.forEach(error => {
        addErrorMessage(formGroup, error)
      })
    }
  }
}

function showErrors (form, rules, errors) {
  Object.keys(rules).forEach(inputName => {
    const inputs = form.querySelectorAll(`[name="${inputName}"]`)

    if (!inputs.length) return

    inputs.forEach((input, inputNumber) => {
      showErrorsForInput(input, errors && errors[inputName], inputNumber)
    })
  })
}

function reEnableDisabledSubmit (form) {
  const submitButton = form.querySelector('[type="submit"]') || document.querySelector(`[type="submit"][form="${form.id}"]`)

  if (!submitButton) {
    return
  }

  // This is a hack to make sure Rails-UJS has disabled the form submit before we
  // re-enable it when errors are found. If we don't do this, for non-async
  // validation, enableElement runs before Rails-UJS so the enabling is canceled and
  // and the form submit is forver disabled :(
  // It looks frail although it seems to consistently work for me.
  // I can't find a better, there doesn't seem to be callbacks from Rails-UJS to use
  // here (┛◉Д◉)┛彡┻━┻
  setTimeout(() => {
    Rails.enableElement(submitButton)
  }, 100)
}

function formSubmitHandling (form, errors, submitEvent, settings) {
  if (errors) {
    reEnableDisabledSubmit(form)
  }

  if (errors && submitEvent) {
    submitEvent.preventDefault()
  } else if (settings.submitHandler) {
    submitEvent.preventDefault()
    settings.submitHandler.call()
  } else if (!settings.skipSubmit && !errors) {
    form.submit()
  }
}

function asyncValidateForm (form, rules, submitEvent, settings = {}) {
  if (submitEvent) {
    submitEvent.preventDefault()
  }

  return validate.async(validate.collectFormValues(form), rules, { fullMessages: false })
    .then(() => {
      if (submitEvent) {
        if (settings.submitHandler) {
          settings.submitHandler.call()
        } else {
          form.submit()
        }
      }
    }).catch((errors) => {
      if (submitEvent) {
        showErrors(form, rules, errors || {})
        formSubmitHandling(form, errors, submitEvent, settings)
      }

      return errors
    })
}

export function validateForm (form, rules, submitEvent, settings = {}) {
  const errors = validate(form, rules, { fullMessages: false })

  showErrors(form, rules, errors || {})
  formSubmitHandling(form, errors, submitEvent, settings)
}

validate.validators.notEmail = function notEmail (value, options) {
  const regex = /^([a-zA-Z0-9_.+-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,6})+$/
  const isEmail = regex.test(value)

  return isEmail ? (options.message || 'No email please') : null
}

validate.validators.lettersOnly = function lettersOnly (value, options) {
  const isLettersOnly = /^[a-zA-ZÀ-ž\-'’\s]*$/.test(value)

  return isLettersOnly ? null : (options.message || 'Letters only please')
}

validate.validators.validPhoneNumber = function validatePhoneNumber (value, options) {
  return new validate.Promise(function validPhoneNumberPromise (resolve) {
    fetch(options.url + '?phone_number=' + encodeURIComponent(value), {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        accept: 'application/json, text/javascript, */*; q=0.01',
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        'x-requested-with': 'XMLHttpRequest'
      },
      redirect: 'error'
    })
      .then((response) => response.json())
      .then(isValidPhoneNumber => {
        if (!isValidPhoneNumber) {
          resolve(options.message)
        }

        /*
         * Resolving the promise with no message tells validate.js the input is valid.
         * There is no concept of rejecting the promise here.
        */
        resolve()
      })
  })
}

validate.validators.validPostcode = function validatePostcode (value, options) {
  return new validate.Promise(function validatePostcodePromise (resolve) {
    fetch(options.url + '?postcode=' + value, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        accept: 'application/json, text/javascript, */*; q=0.01',
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        'x-requested-with': 'XMLHttpRequest'
      },
      redirect: 'error'
    })
      .then((response) => response.json())
      .then(isValidPostcode => {
        if (!isValidPostcode) {
          resolve(options.message)
        }
        resolve()
      })
  })
}

// This is to prevent double submissions on forms if users double-click
// extremely fast. We already use Rails' disable_with method but it seems
// some users can beat it. In addition, we can use the event.detail property
// to definitely prevent double-clicks. Note that event.detail is reset after
// a short time so this won't prevent users to re-click the CTA if validation
// fails.
function preventDoubleSubmit (form) {
  const submitBtn = form.querySelector('[type="submit"]') || document.querySelector(`[type="submit"][form="${form.id}"]`)
  if (!submitBtn) { return }
  submitBtn.addEventListener('click', (event) => {
    if (event.detail && event.detail > 1) {
      event.preventDefault()
    }
  })
}

export default function initFormValidation (form, rules, settings = {}) {
  preventDoubleSubmit(form)

  form.addEventListener('submit', (submitEvent) => {
    if (settings.async) {
      asyncValidateForm(form, rules, submitEvent, settings)
    } else {
      validateForm(form, rules, submitEvent, settings)
    }
  })

  Object.keys(rules).forEach(inputName => {
    const inputs = form.querySelectorAll(`[name="${inputName}"]`)

    if (!inputs.length) return

    inputs.forEach((input, inputNumber) => {
      input.addEventListener('change', function handleInputChange () {
        let errors

        if (settings.async) {
          asyncValidateForm(form, rules).then(asyncErrors => {
            errors = asyncErrors || {}
            showErrorsForInput(this, errors[this.name], inputNumber)
          })
        } else {
          errors = validate(form, rules, { fullMessages: false }) || {}
          showErrorsForInput(this, errors[this.name], inputNumber)
        }
      })
    })
  })
}
